import React, { useMemo } from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { getTruckComplianceMatrixByTruckId } from "../../../api/truckComplianceService";
import { getTrailerComplianceMatrixByTrailerId } from "../../../api/trailerComplianceService";

interface ComplianceSnapshot {
  hasLeaseAgreement: boolean;
  hasCertificateOfInsurance: boolean;
  hasRegistration: boolean;
  hasAnnualInspection: boolean;
}

interface AssetNavigationProps {
  currentId: string;
  assetType: "truck" | "trailer";
}

const AssetNavigation: React.FC<AssetNavigationProps> = ({
  currentId,
  assetType,
}) => {
  const { token } = useAuth();

  const { data: truckCompliance } = useQuery({
    queryKey: ["truckComplianceById", currentId],
    queryFn: () =>
      getTruckComplianceMatrixByTruckId(currentId, token ?? undefined),
    enabled: assetType === "truck" && !!currentId && !!token,
  });

  const { data: trailerCompliance } = useQuery({
    queryKey: ["trailerComplianceById", currentId],
    queryFn: () =>
      getTrailerComplianceMatrixByTrailerId(currentId, token ?? undefined),
    enabled: assetType === "trailer" && !!currentId && !!token,
  });

  const compliance: ComplianceSnapshot | undefined =
    assetType === "truck" ? truckCompliance : trailerCompliance;

  const missingDocuments = useMemo(() => {
    if (!compliance) return [];

    return [
      !compliance.hasLeaseAgreement ? "Lease Agreement" : null,
      !compliance.hasCertificateOfInsurance ? "Certificate of Insurance" : null,
      !compliance.hasRegistration ? "Registration" : null,
      !compliance.hasAnnualInspection ? "Annual Inspection" : null,
    ].filter((item): item is string => Boolean(item));
  }, [compliance]);

  if (!compliance) {
    return null;
  }

  const isCompliant = missingDocuments.length === 0;

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Compliance Status:
        </Typography>
        <Chip
          color={isCompliant ? "success" : "error"}
          icon={isCompliant ? <CheckCircle /> : <Cancel />}
          label={isCompliant ? "Compliant" : "Non-compliant"}
        />
        {!isCompliant && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Missing documents:
            </Typography>
            <Typography variant="body2">
              {missingDocuments.join(", ")}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default AssetNavigation;
